import React, { useState, useRef, useEffect } from "react";
import { NavLink, Link } from "react-router-dom";
import { useAuth, requireAuth } from "utilities/auth";
import { getUserType } from "utilities/userType";
import { useAdminCompany } from "context/AdminCompanyContext";
import { useCompany } from "hooks/useCompany";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";
import UserAvatar from "../UserAvatar";

import {
  Menu,
  X,
  FileText,
  Zap,
  ChevronDown,
  ChevronRight,
  LogOut,
  Building2,
  Repeat,
  MessageSquare,
  Settings,
  CreditCard,
  Plug,
  LayoutDashboard,
  BookOpen,
  BarChart3,
  ShieldAlert,
  ScrollText,
  Ticket,
  LifeBuoy,
  Percent,
  FolderOpen,
  Route as RouteIcon,
  LineChart,
  BellRing,
} from "lucide-react";

const NAV_W_COLLAPSED = 65; // px
const NAV_W_EXPANDED = 280; // px

function VerticalNav() {
  const auth = useAuth();
  const { activeCompanyId, setActiveCompanyId, childCompanies, isParentCompany } = useAdminCompany();

  const [isExpanded, setIsExpanded] = useState(false);
  const [openDropdowns, setOpenDropdowns] = useState(new Set());
  const navRef = useRef(null);

  // Ticketing nav gate (SOUND-493): internal always; client/partner only when
  // their active company has the `ticketing` feature. `requireFeature` enforces
  // this server-side, so this is a nicety, not a security boundary. Internal
  // users skip the fetch (useCompany short-circuits on a null id).
  const navUserType = getUserType(auth.user);
  const navIsInternal = navUserType === "internal";
  const navCompanyId = useEffectiveCompanyId();
  const { company: navCompany } = useCompany(navIsInternal ? null : navCompanyId);
  const ticketingEnabled =
    navIsInternal || (navCompany?.features || []).includes("ticketing");
  // SOUND-709 — same shape as the ticketing gate. requireFeature("credentials")
  // enforces this server-side, so this is a nicety, not a security boundary.
  const credentialsEnabled =
    navIsInternal || (navCompany?.features || []).includes("credentials");

  // Keep a CSS var in sync so AccountLayout can offset with ml-[var(--nav-w)]
  useEffect(() => {
    const w = isExpanded ? NAV_W_EXPANDED : NAV_W_COLLAPSED;
    document.documentElement.style.setProperty("--nav-w", w + "px");
  }, [isExpanded]);

  // Init on mount (in case layout renders before first effect)
  useEffect(() => {
    document.documentElement.style.setProperty(
      "--nav-w",
      NAV_W_COLLAPSED + "px"
    );
  }, []);

  // Outside click / Esc closes when expanded
  useEffect(() => {
    function handlePointerDown(e) {
      if (!isExpanded || !navRef.current) return;
      if (navRef.current.contains(e.target)) return;
      setIsExpanded(false);
      setOpenDropdowns(new Set());
    }
    function handleKeyDown(e) {
      if (e.key === "Escape" && isExpanded) {
        setIsExpanded(false);
        setOpenDropdowns(new Set());
      }
    }
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown, {
      passive: true,
    });
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isExpanded]);


  const toggleDropdown = (label) => {
    const next = new Set(openDropdowns);
    if (next.has(label)) next.delete(label);
    else next.add(label);
    setOpenDropdowns(next);
  };

  // ---------------- NAV DATA (SOUND-244, revised SOUND-681) ----------------
  // Per docs/nav-redesign-context.md: Telegram + Processors demoted into
  // Settings. Wiki + Settings live under "Resources" so they get the same
  // visual treatment as the other sections (instead of orphan standalone
  // items).
  //
  // SOUND-681 (CPO 2026-08-24) dissolved the "Integrations" section: Rerunner
  // and Billing moved DOWN into the Settings left rail, Credentials moved
  // sideways into Resources, and the audit log came UP to CRM as "Change Log".
  // With Rerunner and Credentials both gone, nothing was left in Integrations.
  const adminLinks = [
    {
      section: "CRM",
      items: [
        { to: "/companies", label: "Clients", icon: <Building2 className="h-[18px] w-[18px]" /> },
        { to: "/mid-applications", label: "Applications", icon: <FileText className="h-[18px] w-[18px]" /> },
        { to: "/mids", label: "Portfolio", icon: <CreditCard className="h-[18px] w-[18px]" /> },
        { to: "/tickets", label: "Tickets", icon: <Ticket className="h-[18px] w-[18px]" /> },
        // SOUND-661 — the cross-company document list. INTERNAL ONLY: it is the
        // only surface that reads files outside one company, and this collection
        // holds 175 driver's licences and 247 personal bank statements. A client
        // or partner already has the per-company Files tab, which is the same
        // data correctly scoped, so a global list would add reach without adding
        // capability.
        { to: "/files", label: "Files", icon: <FolderOpen className="h-[18px] w-[18px]" /> },
        // Disputifier enrollment portfolio — hidden from the nav per CPO (2026-07-02).
        // Route /disputifier still works by direct URL; item kept for easy restore.
        // { to: "/disputifier", label: "Disputifier", icon: <Shield className="h-[18px] w-[18px]" /> },
      ],
    },
    {
      // CPO 2026-10-23: the things Sound DOES to client systems, separate from
      // the CRM records. Routing (SOUND-826/827) changes live CheckoutChamp
      // routing; the Change Log is where those changes, and every other
      // configuration change, are recorded.
      section: "Orchestration",
      items: [
        // SOUND-826 — CheckoutChamp gateway routing, mirrored read-only.
        // INTERNAL ONLY by placement: it lives in adminLinks, which only the
        // `internal` userType ever receives (see the switch below) — it is
        // deliberately absent from customerLinks / partnerLinks /
        // processorLinks. The route is <RequireRole role="internal"> and the
        // API is requireRole("internal"), so this is not the security boundary.
        { to: "/routing", label: "Routing", icon: <RouteIcon className="h-[18px] w-[18px]" /> },
        // SOUND-847 — Disputifier alert coverage, gaps and refund rate.
        // Internal only by placement (adminLinks) and by route + API guard.
        { to: "/alerts", label: "Alerts", icon: <BellRing className="h-[18px] w-[18px]" /> },
        // SOUND-681 — was "Audit Log" buried in the Settings rail. Renamed
        // because the name undersold it: this surface carries configuration
        // changes as well as audit history, so "Change Log" is what it is.
        // URL stays /audit — six call sites already navigate to /audit/:logId
        // (AuditLogCard, useCreateAuditLog, AuditLogPage, RuleDetail, the
        // company ActivityTab), so promoting the route removes a redirect hop
        // instead of adding one. Renaming the path would break those links and
        // every bookmark for zero gain.
        { to: "/audit", label: "Change Log", icon: <ScrollText className="h-[18px] w-[18px]" /> },
      ],
    },
    {
      section: "Analytics",
      items: [
        { to: "/analytics", label: "Daily Performance", icon: <BarChart3 className="h-[18px] w-[18px]" />, end: true },
        // Authorization Performance report builder (SOUND-590) — analyst picks
        // the GROUP BY over mart_auth_performance.
        { to: "/analytics/auth", label: "Auth Performance", icon: <Percent className="h-[18px] w-[18px]" /> },
        { to: "/analytics/rerunner", label: "Rerunner Recovery", icon: <Repeat className="h-[18px] w-[18px]" /> },
        // Risk view (SOUND-419) — VAMP/ECM portfolio off presentation.mart_risk.
        // Exposed to the internal nav (CPO 2026-07-27) now that it's live on prod.
        { to: "/risk", label: "Risk", icon: <ShieldAlert className="h-[18px] w-[18px]" /> },
        // LTV (in the nav since SOUND-812): the decomposed model over
        // MART_LTV_COHORT_V2. Internal-only, like the route.
        { to: "/ltv", label: "LTV", icon: <LineChart className="h-[18px] w-[18px]" /> },
      ],
    },
    {
      section: "Resources",
      items: [
        // SOUND-681 — "for now". Credentials sat alone in Integrations once
        // Rerunner left; parking it here beats keeping a one-item section.
        // Revisit if the pipeline-config surfaces grow enough to earn a
        // section of their own again.
        // /rules works by direct URL but stays out of the nav until the
        // rebuild ships — not in active use yet, internal-only QA.
        { to: "/credentials", label: "Credentials", icon: <Plug className="h-[18px] w-[18px]" /> },
        { to: "/wiki", label: "Wiki", icon: <BookOpen className="h-[18px] w-[18px]" /> },
        { to: "/settings", label: "Settings", icon: <Settings className="h-[18px] w-[18px]" /> },
      ],
    },
  ];

  const customerLinks = [
    { to: "/dashboard", label: "Dashboard", icon: <LayoutDashboard className="h-[18px] w-[18px]" /> },
    // SOUND-709 — clients see "Credentials", the same name internal sees. The
    // route was /integrations and the LABEL DIFFERED BY AUDIENCE: internal read
    // "Credentials", clients read "Integrations", same URL. Feature-gated on
    // `credentials` (CPO 2026-08-26).
    ...(credentialsEnabled
      ? [{ to: "/credentials", label: "Credentials", icon: <Plug className="h-[18px] w-[18px]" /> }]
      : []),
    { to: "/rerunner", label: "Rerunner", icon: <Repeat className="h-[18px] w-[18px]" /> },
    { to: "/settings/telegram/subscribers", label: "Telegram", icon: <MessageSquare className="h-[18px] w-[18px]" /> },
    // Support (SOUND-493) — client-facing label for tickets; feature-gated.
    ...(ticketingEnabled
      ? [{ to: "/tickets", label: "Support", icon: <LifeBuoy className="h-[18px] w-[18px]" /> }]
      : []),
    { to: "/wiki", label: "Wiki", icon: <BookOpen className="h-[18px] w-[18px]" /> },
    { to: "/settings", label: "Settings", icon: <Settings className="h-[18px] w-[18px]" /> },
  ];

  // SOUND-293: partner portal consolidated into /companies.
  // Dashboard removed — partners land on Clients.
  const partnerLinks = [
    {
      section: "CRM",
      items: [
        { to: "/companies", label: "Clients", icon: <Building2 className="h-[18px] w-[18px]" /> },
        { to: "/mid-applications", label: "Applications", icon: <FileText className="h-[18px] w-[18px]" /> },
        // Tickets (SOUND-493) — server scopes to the partner's attributed clients.
        ...(ticketingEnabled
          ? [{ to: "/tickets", label: "Tickets", icon: <Ticket className="h-[18px] w-[18px]" /> }]
          : []),
        { to: "/mids", label: "Portfolio", icon: <CreditCard className="h-[18px] w-[18px]" /> },
      ],
    },
    {
      section: "Integrations",
      items: [
        // SOUND-709 — the Credentials item was REMOVED here.
        // CPO 2026-07-30: partners can never see client stored credentials —
        // not the values, and NOT THE RECORDS. The API now returns a no-match
        // filter for partners and the `credentials` flag is deliberately not
        // granted to them, so this item led to a 403. A nav entry that
        // advertises a capability the bearer does not have is worse than no
        // entry: it invites a support ticket asking why it is broken.
      ],
    },
    {
      section: "Resources",
      items: [
        { to: "/wiki", label: "Wiki", icon: <BookOpen className="h-[18px] w-[18px]" /> },
        { to: "/settings", label: "Settings", icon: <Settings className="h-[18px] w-[18px]" /> },
      ],
    },
  ];

  // SOUND-563: processors get a scoped, read-only monitoring surface — the risk
  // dashboard + their acquired-MID portfolio. Deliberately NO CRM (Clients,
  // Applications, Tickets, Integrations, Settings): a processor cares about
  // MIDs/risk only, and the API is default-deny for everything else.
  const processorLinks = [
    {
      section: "Monitoring",
      items: [
        { to: "/risk", label: "Risk", icon: <ShieldAlert className="h-[18px] w-[18px]" /> },
        { to: "/mids", label: "Portfolio", icon: <CreditCard className="h-[18px] w-[18px]" /> },
      ],
    },
  ];

  let navLinks = [];
  const effectiveUserType = getUserType(auth.user);

  switch (effectiveUserType) {
    case "internal":
      navLinks = adminLinks;
      break;
    case "client":
      navLinks = customerLinks;
      break;
    case "partner":
      navLinks = partnerLinks;
      break;
    case "processor":
      navLinks = processorLinks;
      break;
    default:
      navLinks = [];
  }

  // ---------------- RENDERERS ----------------
  const labelClasses =
    "text-[13px] font-medium whitespace-nowrap transition-colors";

  // SOUND-244: compact spacing — py-2.5 (was py-3). CPO 2026-10-23 condensed it
  // again to py-2, alongside 13px type and 18px icons: py-2 felt too tight at
  // 14px/20px, but not at the smaller size.
  const itemBase =
    "w-full flex items-center pr-4 py-2 transition-colors rounded-md";

  const iconWrap = "flex items-center gap-2.5";

  const collapsedJustify = isExpanded ? "justify-start" : "justify-center";
  const paddingLeft = (depth) => (depth > 0 ? "pl-12" : "pl-4");

  const renderNavItem = (item, depth = 0) => {
    const hasChildren =
      Array.isArray(item.children) && item.children.length > 0;
    const isDropdownOpen = openDropdowns.has(item.label);

    if (hasChildren) {
      return (
        <div key={item.label} className="px-2">
          <button
            onClick={() => {
              if (isExpanded) toggleDropdown(item.label);
              else {
                setIsExpanded(true);
                toggleDropdown(item.label);
              }
            }}
            className={`${itemBase} ${paddingLeft(
              depth
            )} ${collapsedJustify} text-muted-foreground hover:bg-muted/60`}
          >
            <div className={`${iconWrap} group`}>
              <span className="text-muted-foreground group-hover:text-brand">
                {item.icon}
              </span>
              {isExpanded && (
                <span className={`${labelClasses} group-hover:text-foreground`}>
                  {item.label}
                </span>
              )}
            </div>
            {isExpanded && (
              <div className="ml-auto text-muted-foreground">
                {isDropdownOpen ? (
                  <ChevronDown className="h-[18px] w-[18px]" />
                ) : (
                  <ChevronRight className="h-[18px] w-[18px]" />
                )}
              </div>
            )}
          </button>

          {isExpanded && isDropdownOpen && (
            <div className="overflow-hidden transition-all duration-200 ease-out">
              {item.children.map((child) => renderNavItem(child, depth + 1))}
            </div>
          )}
        </div>
      );
    }

    return (
      <div key={item.label} className="px-2">
        <NavLink
          to={item.to}
          end={item.end}
          className={({ isActive }) =>
            [
              itemBase,
              paddingLeft(depth),
              collapsedJustify,
              isActive
                ? "bg-brand/10 border-r-2 border-brand text-foreground"
                : "text-muted-foreground hover:bg-muted/60",
            ].join(" ")
          }
        >
          <div className={`${iconWrap} group`}>
            <span className="text-muted-foreground group-hover:text-brand">
              {item.icon}
            </span>
            {isExpanded && (
              <span className={`${labelClasses} group-hover:text-foreground`}>
                {item.label}
              </span>
            )}
          </div>
        </NavLink>
      </div>
    );
  };

  // ---------------- VIEW ----------------
  return (
    <header
      ref={navRef}
      className="
        fixed inset-y-0 left-0 z-50
        bg-card text-card-foreground
        border-r border-border
        transition-[width,transform] duration-300 ease-in-out
        flex flex-col
      "
      style={{ width: "var(--nav-w, 65px)" }}
      aria-label="Primary"
    >
      {/* Top: logo + collapse button */}
      <div className="flex items-center justify-between px-4 mb-6 mt-4">
        <NavLink
          to="/"
          className={[
            "text-xl font-semibold tracking-tight",
            isExpanded ? "" : "w-full text-center",
          ].join(" ")}
        >
          {isExpanded ? (
            <>
              Sound Commerce<span className="text-brand">.</span>
            </>
          ) : (
            <>
              S<span className="text-brand">.</span>
            </>
          )}
        </NavLink>

        {isExpanded && (
          <button
            onClick={() => {
              setIsExpanded(false);
              setOpenDropdowns(new Set());
            }}
            className="p-1 text-muted-foreground hover:text-foreground transition"
            aria-label="Collapse navigation"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Expand button (collapsed only) */}
      {!isExpanded && (
        <button
          onClick={() => setIsExpanded(true)}
          className="mx-4 mb-4 p-2 rounded-md text-muted-foreground hover:text-brand hover:bg-muted/60 transition"
          aria-label="Open navigation"
        >
          <Menu className="h-5 w-5 mx-auto" />
        </button>
      )}

      {/* Nav links */}
      <nav className="flex-1 overflow-y-auto">
        <div className="space-y-1">
          {navLinks.map((entry) =>
            entry.section ? (
              <div key={entry.section}>
                {isExpanded && (
                  <div className="px-4 pt-3 pb-1 text-xs text-muted-foreground uppercase tracking-wider font-semibold">
                    {entry.section}
                  </div>
                )}
                {entry.items.map((item) => renderNavItem(item))}
              </div>
            ) : (
              renderNavItem(entry)
            )
          )}
        </div>

        {auth.user.featureFlag === "beta" && (
          <div className="px-2 mt-2">
            <div
              className={`flex items-center ${collapsedJustify} px-4 py-2 rounded-md`}
            >
              <Zap className="h-[18px] w-[18px] text-yellow-500" />
              {isExpanded && (
                <span className="ml-3 text-yellow-600 dark:text-yellow-400 text-[13px] font-medium">
                  Beta Features
                </span>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Footer section */}
      <div className="mt-auto space-y-2 px-2 pb-4">
        <Link to="/settings/profile">
          <UserAvatar user={auth.user} isExpanded={isExpanded} />
        </Link>

        {/* Company switcher (parent company clients) */}
        {effectiveUserType === "client" &&
          isParentCompany &&
          isExpanded && (
            <div className="mt-2 border-t border-border pt-3 px-2">
              <label className="text-xs text-muted-foreground mb-2 block">
                Company
              </label>
              <select
                className="input text-xs"
                value={activeCompanyId || ""}
                onChange={(e) => setActiveCompanyId(e.target.value || null)}
              >
                <option value="">My Portfolio</option>
                {childCompanies.map((child) => (
                  <option key={child.companyId} value={child.companyId}>
                    {child.name}
                  </option>
                ))}
              </select>
            </div>
          )}

        <div className="border-t border-border pt-2">
          <Link
            to="/auth/signout"
            onClick={(e) => {
              e.preventDefault();
              auth.signout();
            }}
            className={[
              "flex items-center px-4 py-2 rounded-md transition",
              collapsedJustify,
              "text-muted-foreground hover:text-destructive hover:bg-destructive/10",
            ].join(" ")}
          >
            <LogOut className="h-[18px] w-[18px]" />
            {isExpanded && (
              <span className="ml-3 text-[13px] font-medium">Log out</span>
            )}
          </Link>
        </div>
      </div>
    </header>
  );
}

export default requireAuth(VerticalNav, false);
